import { ChangeEvent, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { gameService } from "services/game/game.service"

interface GameImageOverrideModalProps {
  open: boolean
  game: {
    identifier: string
    title: string
    image: string | null
    image_vertical: string | null
    image_vertical_override: string | null
  } | null
  onClose: () => void
  onSaved: (newOverrideUrl: string | null) => void
}

const ALLOWED_MIME = ["image/png", "image/jpeg", "image/jpg", "image/webp"]
const MAX_BYTES = 5 * 1024 * 1024

export const GameImageOverrideModal = ({ open, game, onClose, onSaved }: GameImageOverrideModalProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [uploading, setUploading] = useState(false)
  const [resetting, setResetting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (open) {
      setUploading(false)
      setResetting(false)
    }
  }, [open, game?.identifier])

  if (!game) return null

  const hasOverride = !!game.image_vertical_override
  const effectiveImage = game.image_vertical || game.image

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return

    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error(String(t("gameManagement.imageOverride.invalidMime", { defaultValue: "Image must be PNG, JPG, or WebP" })))
      return
    }
    if (file.size > MAX_BYTES) {
      toast.error(String(t("gameManagement.imageOverride.tooLarge", { defaultValue: "Image must be 5MB or smaller" })))
      return
    }

    setUploading(true)
    const newUrl = await gameService.uploadGameImageVerticalOverride(game.identifier, file)
    setUploading(false)
    if (newUrl) {
      toast.success(String(t("gameManagement.imageOverride.savedToast", { defaultValue: "Image override saved" })))
      onSaved(newUrl)
    }
  }

  const handleReset = async () => {
    if (!hasOverride || resetting) return
    setResetting(true)
    const ok = await gameService.resetGameImageVerticalOverride(game.identifier)
    setResetting(false)
    if (ok) {
      toast.success(String(t("gameManagement.imageOverride.revertedToast", { defaultValue: "Reverted to provider image" })))
      onSaved(null)
    }
  }

  const busy = uploading || resetting

  return (
    <Modal open={open} handler={onClose}>
      <div className="modal-box w-[min(560px,95vw)] text-[rgba(6,7,21,1)] dark:text-white">
        <header className="flex flex-col gap-1 mb-4">
          <h3 className="font-bold text-lg">{t("gameManagement.imageOverride.title", { defaultValue: "Game image" })}</h3>
          <p className="text-sm opacity-70 break-words">
            {game.title} · <span className="font-mono">{game.identifier}</span>
          </p>
        </header>

        <div className="flex flex-col items-center gap-3 mb-4">
          {effectiveImage ? (
            <img
              src={effectiveImage}
              alt={game.title}
              className="h-48 w-auto rounded border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.05]"
            />
          ) : (
            <div className="h-48 w-32 rounded border border-dashed border-black/20 dark:border-white/20 flex items-center justify-center text-xs opacity-60">
              {t("gameManagement.imageOverride.noImage", { defaultValue: "No image" })}
            </div>
          )}
          <div className="text-xs opacity-70">
            {hasOverride ? (
              <span className="text-[rgba(132,_0,_168,_1)] dark:text-[rgba(169,_78,_206,_1)] font-medium">
                {t("gameManagement.imageOverride.activeNote", {
                  defaultValue: "Custom override active — provider syncs will not change this."
                })}
              </span>
            ) : (
              <span>
                {t("gameManagement.imageOverride.providerNote", { defaultValue: "Provider image — replaced if the provider updates it on sync." })}
              </span>
            )}
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept={ALLOWED_MIME.join(",")} className="hidden" onChange={handleFileChange} />

        <div className="modal-action gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className={clsx("btn btn-outline", "border-[rgba(132,_0,_168,_0.5)] text-[rgba(132,_0,_168,_1)] dark:text-white")}
            onClick={handleReset}
            disabled={!hasOverride || busy}
            title={
              hasOverride
                ? t("gameManagement.imageOverride.resetTip", { defaultValue: "Drop the override and fall back to the provider image" })
                : t("gameManagement.imageOverride.noResetTip", { defaultValue: "No override to reset" })
            }
          >
            {resetting
              ? t("gameManagement.imageOverride.resetting", { defaultValue: "Resetting…" })
              : t("gameManagement.imageOverride.resetCta", { defaultValue: "Reset to provider" })}
          </button>
          <button
            type="button"
            className={clsx("btn btn-primary", "bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent")}
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {uploading
              ? t("gameManagement.imageOverride.uploading", { defaultValue: "Uploading…" })
              : t("gameManagement.imageOverride.uploadCta", { defaultValue: "Upload new" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
